// Chord-change trainer: the "one-minute changes" drill on this song's own
// chord changes, hardest first. Switch between two chords for a minute and
// count the clean changes; the microphone can do the counting. The best
// score for each pair (in changes a minute, so rounds of any length compare)
// is kept so players can see themselves getting faster.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { parseChord } from '../backing-track/chords';
import { ChangeCounter, difficultyLevel, songChanges } from './changes';
import { track } from '../../usage';
import { fetchChangeScores, saveChangeScore } from './practice-api';
import { chordNotes } from './strum';
import { ChordDiagram, MicLevel, Segmented, micProblem } from './controls';
import { isMicrophoneSupported } from './audio';
import { KeyHint, chordNumber, isShortcut, spaceLabel } from './shortcuts';

const DURATIONS = [30, 60, 120];
const COUNT_IN = 3;

const difficultyName = level => [gettext('Easy'), gettext('Medium'), gettext('Hard')][level - 1];

const perMinute = (changes, seconds) => Math.round(changes * 60 / seconds);
const bestName = best => interpolate(gettext('Best: %s a minute'), [best]);

const durationName = seconds => (seconds < 60 ?
    interpolate(gettext('%s sec'), [seconds]) :
    interpolate(gettext('%s min'), [seconds / 60]));

export default function ChangesTrainer({
    songId,
    chordNames,
    chordsByName,
    tuning,
    player,
    capo,
    instrumentId,
    isSubscriber,
    isAuthenticated,
    onRequireSubscription,
    onRunningChange,
    onBeforeStart,
}) {
    const pairs = useMemo(() => songChanges(chordNames, chordsByName), [chordNames, chordsByName]);
    const [selected, setSelected] = useState(null);
    const [duration, setDuration] = useState(60);
    const [phase, setPhase] = useState('ready');
    const [countIn, setCountIn] = useState(COUNT_IN);
    const [remaining, setRemaining] = useState(0);
    const [state, setState] = useState({ chord: null, changes: 0, level: 0 });
    const [listen, setListen] = useState(isSubscriber && isMicrophoneSupported());
    const [micState, setMicState] = useState('off');
    const [scores, setScores] = useState({});
    const [result, setResult] = useState(null);
    const counterRef = useRef(null);
    // The scores as they are when a round ends (they can load mid-round).
    const scoresRef = useRef(scores);
    scoresRef.current = scores;
    const timerRef = useRef(null);
    const runRef = useRef(0);

    const pair = pairs.find(p => p.key === selected) || pairs[0];

    useEffect(() => {
        if (!isAuthenticated || pairs.length === 0) return;
        fetchChangeScores(pairs.map(p => p.key), instrumentId)
            .then(response => setScores(current => ({ ...response.scores, ...current })))
            .catch(() => {});
    }, [isAuthenticated, instrumentId, pairs]);

    const running = phase === 'countdown' || phase === 'running';
    useEffect(() => {
        onRunningChange(running);
    }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

    const finish = () => {
        clearInterval(timerRef.current);
        if (counterRef.current) counterRef.current.stop();
        counterRef.current = null;
        setMicState('off');
    };

    useEffect(() => () => {
        runRef.current += 1;
        finish();
        onRunningChange(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cancel = () => {
        runRef.current += 1;
        finish();
        setPhase('ready');
    };

    const record = changes => {
        const previous = scoresRef.current[pair.key] || {};
        const rate = perMinute(changes, duration);
        const best = Math.max(previous.best || 0, rate);
        setResult({ changes, isBest: rate > 0 && rate > (previous.best || 0) });
        setScores(current => ({ ...current, [pair.key]: { ...previous, best, last: rate } }));
        if (isAuthenticated && changes > 0) {
            saveChangeScore({ a: pair.a, b: pair.b, changes, seconds: duration, songId, instrumentId }).catch(() => {});
        }
        track('changes_round_finished', { mode: listen ? 'listen' : 'tap', seconds: duration });
    };

    const start = async () => {
        if (!pair) return;
        const run = ++runRef.current;
        onBeforeStart();
        setResult(null);
        setState({ chord: null, changes: 0, level: 0 });
        const counter = new ChangeCounter(pair.a, pair.b, setState, { capo });
        counterRef.current = counter;
        if (listen) {
            setMicState('requesting');
            try {
                await counter.start();
                setMicState('on');
            } catch (e) {
                if (run !== runRef.current) return;
                setMicState(e && e.name === 'NotAllowedError' ? 'denied' : 'error');
            }
        }
        if (run !== runRef.current) return;
        // 3, 2, 1, go: listening already, so the first chord is known.
        setPhase('countdown');
        setCountIn(COUNT_IN);
        let left = COUNT_IN;
        player.beep('tick');
        timerRef.current = setInterval(() => {
            left -= 1;
            if (left > 0) {
                setCountIn(left);
                player.beep('tick');
                return;
            }
            clearInterval(timerRef.current);
            player.beep('go');
            counter.go();
            setState(s => ({ ...s, changes: 0 }));
            setPhase('running');
            const end = performance.now() + duration * 1000;
            setRemaining(duration);
            timerRef.current = setInterval(() => {
                const left = Math.max(0, Math.ceil((end - performance.now()) / 1000));
                setRemaining(left);
                if (left === 0) {
                    const { changes } = counter;
                    finish();
                    player.beep('end');
                    setPhase('done');
                    record(changes);
                }
            }, 200);
        }, 1000);
    };

    // Count by hand: the button, or the space bar while a round runs.
    const add = () => {
        if (phase === 'running' && counterRef.current) counterRef.current.add();
    };
    // Space starts a round, and counts a change during one. 1 and 2 play
    // the two chords between rounds (during one the microphone would count
    // them).
    useEffect(() => {
        if (phase === 'countdown') return undefined;
        const onKey = e => {
            if (e.repeat || !isShortcut(e)) return;
            const n = chordNumber(e.key);
            if (n === 1 || n === 2) {
                if (phase === 'running' || !pair) return;
                e.preventDefault();
                hear(n === 1 ? pair.a : pair.b);
                return;
            }
            if (e.key !== ' ') return;
            e.preventDefault();
            if (phase === 'running') {
                add();
            } else {
                start();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const chooseListen = value => {
        if (value && !isSubscriber) {
            onRequireSubscription();
            return;
        }
        setListen(value);
    };

    const notesOf = name => chordNotes(parseChord(name), chordsByName[name], tuning);
    const hear = name => player.strum(notesOf(name));

    useEffect(() => {
        if (pair) player.preload([...notesOf(pair.a), ...notesOf(pair.b)]);
    }, [pair && pair.key, chordsByName, tuning]); // eslint-disable-line react-hooks/exhaustive-deps

    if (pairs.length === 0) {
        return <div className="changes-trainer practice-tool"><p className="practice-hint">{gettext('This song has no chord changes to practise.')}</p></div>;
    }

    const score = scores[pair.key] || {};
    const problem = listen ? micProblem(micState) : null;

    return (
        <div className="changes-trainer practice-tool">
            <div className="changes-pair">
                {[pair.a, pair.b].map((name, i) => (
                    <ChordDiagram
                        key={name}
                        name={name}
                        chord={chordsByName[name]}
                        className={classNames('large', { heard: running && listen && state.chord === i })}
                        onClick={running ? undefined : () => hear(name)}
                        shortcut={running ? undefined : String(i + 1)}
                    />
                ))}
                <div className="changes-counter" aria-live="polite">
                    {phase === 'countdown' && <span className="changes-big">{countIn}</span>}
                    {phase === 'running' && <>
                        <span className="changes-big">{state.changes}</span>
                        <span className="changes-time">{remaining}s</span>
                    </>}
                    {phase === 'done' && result && <>
                        <span className="changes-big">{result.changes}</span>
                        <span className={classNames('changes-result', { best: result.isBest })}>
                            {result.isBest ? gettext('New best!') : bestName(score.best || 0)}
                        </span>
                    </>}
                    {phase === 'ready' && <>
                        <span className="changes-best">{score.best ? bestName(score.best) : gettext('No score yet')}</span>
                    </>}
                </div>
            </div>

            <div className="practice-row">
                {running ?
                    <>
                        <button type="button" className="practice-play playing" onClick={cancel} aria-label={gettext('Stop')}><span className="icon-stop" /></button>
                        {phase === 'running' && (!listen || micState !== 'on') &&
                            <button type="button" className="practice-pill changes-add" onClick={add}>+1<KeyHint k={spaceLabel()} /></button>
                        }
                        {phase === 'running' && listen && micState === 'on' &&
                            <>
                                <MicLevel level={state.level} />
                                <button type="button" className="practice-pill" onClick={add} title={gettext('Count one it missed')}>+1<KeyHint k={spaceLabel()} /></button>
                            </>
                        }
                    </>
                    :
                    <>
                        <button type="button" className="practice-play" onClick={start} aria-label={gettext('Start')}><span className="icon-play" /><KeyHint k={spaceLabel()} /></button>
                        <Segmented
                            label={gettext('How long')}
                            value={duration}
                            onChange={setDuration}
                            options={DURATIONS.map(seconds => ({ value: seconds, label: durationName(seconds) }))}
                        />
                    </>
                }
            </div>

            {!running &&
                <div className="practice-row">
                    <Segmented
                        label={gettext('Counting')}
                        value={listen}
                        onChange={chooseListen}
                        options={[
                            { value: true, label: gettext('Listen and count') },
                            { value: false, label: gettext('I tap +1') },
                        ]}
                    />
                </div>
            }

            {problem && <p className="practice-hint">{problem}</p>}
            {phase === 'ready' && <p className="practice-hint">
                {listen ?
                    gettext('Switch between the two chords, strumming once each time, for as many clean changes as you can.') :
                    gettext('Switch between the two chords and tap +1 (or the space bar) for each clean change.')}
            </p>}
            {!isAuthenticated && <p className="practice-hint">{gettext('Log in to keep your best scores.')}</p>}

            <ul className="changes-list" aria-label={gettext("This song's chord changes")}>
                {pairs.map(p => {
                    const level = difficultyLevel(p.difficulty);
                    const best = scores[p.key] && scores[p.key].best;
                    return (
                        <li key={p.key}>
                            <button type="button" className={classNames({ active: p.key === pair.key })} disabled={running} onClick={() => { setSelected(p.key); setPhase('ready'); setResult(null); }}>
                                <span className="changes-name">{p.a} ⇄ {p.b}</span>
                                <span className={`changes-level level-${level}`} title={difficultyName(level)}>{difficultyName(level)}</span>
                                <span className="changes-count">{interpolate(ngettext('%s time in the song', '%s times in the song', p.count), [p.count])}</span>
                                <span className="changes-score">{best ? bestName(best) : ''}</span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
